import React from 'react';
import { Trash2 } from 'lucide-react';

// Editable review table for credit-sale lines pulled from a Tally import (or typed manually)
// before they're saved. Lets you fix vehicle/voucher/product/qty/rate/amount/cash on any row.
// Fixed layout so the whole table fits the page - no sideways scrolling. Each column has a minimum
// width (px) that its content needs (a date box needs ~100px, a voucher no. ~110px); the table is
// stretched to the page width and the spare space is shared out. Only when the page is narrower
// than the sum is there genuinely no room - a scroll bar then beats clipped numbers.
// showParty={false} drops the Party column where every line is the same creditor (the Invoice page).
//               date  party veh voucher item qty rate amount cash  del
const WIDTHS_WITH_PARTY = [104, 120, 78, 112, 80, 62, 60, 72, 72, 36];
const WIDTHS_WITHOUT_PARTY = [104, 78, 112, 80, 62, 60, 72, 72, 36];
const sum = (a) => a.reduce((x, y) => x + y, 0);

// number inputs: no spinner arrows eating into the width
const noSpin = '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

const inputBase = 'w-full min-w-0 bg-transparent border border-transparent hover:border-slate-200 focus:border-indigo-400 rounded px-1 py-1.5 text-xs';

const CreditEntryReviewTable = ({ entries, onChangeField, onChangeItemName, onDelete, flags = {}, showParty = true }) => {
  const widths = showParty ? WIDTHS_WITH_PARTY : WIDTHS_WITHOUT_PARTY;
  const minWidth = sum(widths);
  const handleChange = (id, field, value) => {
    if (['quantity', 'rate', 'amount', 'cash_amount'].includes(field)) {
      onChangeField(id, field, value === '' ? '' : parseFloat(value));
    } else {
      onChangeField(id, field, value);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed text-left border-collapse" style={{ minWidth }}>
        <colgroup>
          {widths.map((w, i) => <col key={i} style={{ width: `${w}px` }} />)}
        </colgroup>
        <thead>
          <tr className="bg-slate-50 text-slate-500 text-xs border-b border-slate-200">
            <th className="p-2 font-medium">Date</th>
            {showParty && <th className="p-2 font-medium">Party</th>}
            <th className="p-2 font-medium">Vehicle</th>
            <th className="p-2 font-medium">Voucher No.</th>
            <th className="p-2 font-medium">Item</th>
            <th className="p-2 font-medium text-right">Qty (Ltr)</th>
            <th className="p-2 font-medium text-right">Rate</th>
            <th className="p-2 font-medium text-right">Amount (₹)</th>
            <th className="p-2 font-medium text-right">Cash (₹)</th>
            <th className="p-2 font-medium text-center"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {entries.map((entry) => (
            <React.Fragment key={entry.id}>
            <tr className={`transition-colors ${(flags[entry.id] || []).some(f => f.level === 'error') ? 'bg-red-50' : (flags[entry.id] || []).length ? 'bg-amber-50/70' : 'hover:bg-slate-50/50'}`}>
              <td className="p-1">
                <input type="date" className={`${inputBase} text-[11px]`}
                  value={entry.business_date || ''} onChange={e => handleChange(entry.id, 'business_date', e.target.value)} />
              </td>
              {showParty && <td className="p-1 text-xs text-slate-600 truncate" title={entry.party_name}>{entry.party_name}</td>}
              <td className="p-1">
                <input type="text" className={`${inputBase} font-mono`}
                  value={entry.vehicle_no || ''} onChange={e => handleChange(entry.id, 'vehicle_no', e.target.value)} />
              </td>
              <td className="p-1">
                <input type="text" className={`${inputBase} font-mono`} title={entry.voucher_no || ''}
                  value={entry.voucher_no || ''} onChange={e => handleChange(entry.id, 'voucher_no', e.target.value)} />
              </td>
              <td className="p-1">
                <select className={inputBase}
                  value={entry.item_name || ''} onChange={e => (onChangeItemName || ((id, v) => handleChange(id, 'item_name', v)))(entry.id, e.target.value)}>
                  <option value="">-</option>
                  <option value="Petrol">Petrol</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Xtragreen">Xtragreen</option>
                  <option value="CASH">CASH</option>
                </select>
              </td>
              <td className="p-1">
                <input type="number" className={`${inputBase} ${noSpin} text-right font-mono`}
                  value={entry.quantity ?? ''} onChange={e => handleChange(entry.id, 'quantity', e.target.value)} />
              </td>
              <td className="p-1">
                <input type="number" step="0.01" className={`${inputBase} ${noSpin} text-right font-mono`}
                  value={entry.rate ?? ''} onChange={e => handleChange(entry.id, 'rate', e.target.value)} />
              </td>
              <td className="p-1">
                <input type="number" className={`${inputBase} ${noSpin} text-right font-mono font-medium`}
                  value={entry.amount ?? ''} onChange={e => handleChange(entry.id, 'amount', e.target.value)} />
              </td>
              <td className="p-1">
                <input type="number" className={`${inputBase} ${noSpin} text-right font-mono font-medium text-amber-700`}
                  value={entry.cash_amount ?? ''} onChange={e => handleChange(entry.id, 'cash_amount', e.target.value)} />
              </td>
              <td className="p-1 text-center">
                <button
                  onClick={() => onDelete(entry.id)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
            {(flags[entry.id] || []).length > 0 && (
              <tr className={(flags[entry.id] || []).some(f => f.level === 'error') ? 'bg-red-50' : 'bg-amber-50/70'}>
                <td colSpan={widths.length} className="px-3 pb-2 pt-0 text-xs">
                  {flags[entry.id].map((f, i) => (
                    <div key={i} className={f.level === 'error' ? 'text-red-700 font-medium' : 'text-amber-800'}>
                      {f.level === 'error' ? '✖' : '⚠'} {f.message}
                    </div>
                  ))}
                </td>
              </tr>
            )}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot className="bg-slate-50 border-t border-slate-200">
          <tr>
            <td colSpan={showParty ? 7 : 6} className="p-3 text-right font-bold text-slate-700">Total:</td>
            <td className="p-3 text-right font-bold text-indigo-700 font-mono">
              {entries.reduce((sum, e) => sum + (e.amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </td>
            <td className="p-3 text-right font-bold text-amber-700 font-mono">
              {entries.reduce((sum, e) => sum + (e.cash_amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default CreditEntryReviewTable;
